import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaInfoCircle, FaQuestionCircle, FaEnvelope, FaSignOutAlt, FaThLarge } from 'react-icons/fa';
import api from '../../utils/api';
import './LandingPage.css';
import Footer from './Footer';
import LanguageSelector from '../Common/LanguageSelector';
import KaroTrukkLogo from '../Common/KaroTrukkLogo';
import { useLanguage } from '../../context/LanguageContext';

import dashboardPreview from '../../assets/dashboard-preview.webp';
import gpsProduct from '../../assets/mainminiexc.webp';
import waterTankerImg from '../../assets/water-tanker.webp';


import EquipmentDropdown from './EquipmentDropdown';
import RestrictionPopup from '../Shared/RestrictionPopup';

// Redeploy trigger: Force fresh build

// Equipment Data
// Equipment Data
const equipmentData = [
  { id: 1, title: 'Scissor Lifts', category: 'Scissor Lifts', image: '/images/equipment/scissor-lift.webp' },
  { id: 10, title: 'Generators', category: 'Generators', image: '/images/equipment/generator.webp' },
  { id: 2, title: 'Boom Lifts', category: 'Boom Lifts', image: '/images/equipment/boom-lift.webp' },
  { id: 3, title: 'Forklifts & Material Handling', category: 'Forklifts', image: '/images/equipment/forklift.webp' },
  { id: 4, title: 'Single Man Lifts', category: 'Single Man Lifts', image: '/images/equipment/single-man-lift.webp' },
  { id: 5, title: 'Earthmoving Equipment', category: 'Earthmoving', image: '/images/equipment/earthmoving.webp' },
  { id: 6, title: 'Compaction Equipment', category: 'Compaction', image: '/images/equipment/compaction.webp' },
  { id: 7, title: 'Cranes', category: 'Cranes', image: '/images/equipment/crane.webp' },
];

// Testimonial Data
const testimonials = [
  {
    id: 1,
    text: "KaroTrukk has simplified our logistics significantly. Reliable and fast!",
    author: "Javid Hussain",
    role: "Lucky associates",
    icon: "🤝"
  },
  {
    id: 2,
    text: "Excellent service and real-time tracking. Highly recommended for heavy vehicles.",
    author: "Yogi",
    role: "YOGI movers",
    icon: "🚛"
  },
  {
    id: 3,
    text: "The best platform for booking machinery. Easy to use and transparent.",
    author: "Khaader Hussaian",
    role: "Gold agro company",
    icon: "🚜"
  },
  {
    id: 4,
    text: "Professional booking experience for our industrial transport needs.",
    author: "Hari Thiagarajan",
    role: "Thiagarajar mills",
    icon: "🏗️"
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showRestriction, setShowRestriction] = useState(false);
  const [restrictionMessage, setRestrictionMessage] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const scrollRef = useRef(null);
  const menuRef = useRef(null);

  const handleCardScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll > 0) {
      setScrollProgress((scrollLeft / maxScroll) * 100);
    }
  };

  const handleTouchBarClick = (e) => {
    if (!scrollRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const { scrollWidth, clientWidth } = scrollRef.current;
    scrollRef.current.scrollTo({
      left: (scrollWidth - clientWidth) * percentage,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const token = localStorage.getItem('token');

    if (loggedIn && token) {
      // Verify token in background
      api.get('/api/auth/me')
        .then((res) => {
          if (res.data.success) {
            setIsLoggedIn(true);
            setUser(res.data.user);
            localStorage.setItem('user', JSON.stringify(res.data.user));
          }
        })
        .catch(() => {
          // Token is dead or user deleted
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('user');
          localStorage.removeItem('userType');
          localStorage.removeItem('token');
          setIsLoggedIn(false);
          setUser(null);
        });
    } else {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('user');
        localStorage.removeItem('userType');
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        setUser(null);
    }

    // Click outside listener
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const getCurrentRole = () => {
    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    const role = localUser.role || user?.role;
    const type = localStorage.getItem('userType');
    
    // Strict priority: Always trust the role on the user object first
    if (role === 'fleet-partner') return 'fleet-partner';
    if (role === 'client') return 'client';
    
    // Fallback to type if role is missing
    if (type === 'vehicle-owner' || localUser.userType === 'vehicle-owner') return 'fleet-partner';
    if (type === 'customer' || type === 'business' || localUser.userType === 'customer' || localUser.userType === 'business') return 'client';
    
    return null;
  };

  const navigateToDashboard = () => {
    const role = getCurrentRole();
    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    const isBusiness = localUser.userType === 'business' || localStorage.getItem('userType') === 'business';

    if (role === 'client') {
      navigate(isBusiness ? '/business/dashboard' : '/client/dashboard');
    } else if (role === 'fleet-partner') {
      navigate('/fleet-partner/dashboard');
    } else {
      handleLogout();
      alert('Session expired or invalid. Please login again.');
    }
  };

  const handleClientOnlyAction = (targetPath) => {
    const role = getCurrentRole();

    if (isLoggedIn && role === 'fleet-partner') {
      setRestrictionMessage(t('onlyClientCanBook'));
      setShowRestriction(true);
      return;
    }

    if (isLoggedIn) {
      navigate(targetPath);
    } else {
      navigate('/login', { state: { from: targetPath, role: 'client' } });
    }
  };

  const handleClientAction = () => {
    const role = getCurrentRole();

    if (isLoggedIn) {
      if (role === 'client') {
        navigateToDashboard();
      } else if (role === 'fleet-partner') {
        setRestrictionMessage(t('onlyClientCanBook'));
        setShowRestriction(true);
      } else {
        handleLogout();
        navigate('/login');
      }
    } else {
      navigate('/signup', { state: { role: 'client' } });
    }
  };

  const handleFleetPartnerAction = () => {
    const role = getCurrentRole();

    if (isLoggedIn) {
      if (role === 'fleet-partner') {
        navigate('/fleet-partner/dashboard');
      } else if (role === 'client') {
        setRestrictionMessage('You logged in as a client.');
        setShowRestriction(true);
      } else {
        handleLogout();
        navigate('/vehicle-owner/login');
      }
    } else {
      navigate('/fleet-partner/register');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    setUser(null);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="landing-page font-sans text-gray-900">
      {/* Fixed Top Bar */}
      <header className="fixed top-0 w-full z-50 bg-black/95 backdrop-blur-xl border-b border-gray-800 text-white shadow-lg pt-safe">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between relative">
          {/* Logo */}
          <div className="flex items-center cursor-pointer h-full py-1" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <KaroTrukkLogo className="h-5 sm:h-6 w-auto hover:opacity-90 transition-opacity" color="#FFC72C" />
          </div>

          {/* Unified Desktop Navigation Group (32px uniform gaps, strict vertical centering) */}
          <nav className="hidden md:flex items-center gap-8 h-full">
            <EquipmentDropdown 
              equipmentData={equipmentData} 
              isLoggedIn={isLoggedIn} 
              onAction={handleClientOnlyAction}
            />
            {!isLoggedIn && (
              <>
                <button onClick={handleClientAction} className="text-sm font-semibold text-gray-200 hover:text-white transition leading-none">
                  {t('client')}
                </button>
                <button onClick={handleFleetPartnerAction} className="text-sm font-semibold text-gray-200 hover:text-white transition leading-none">
                  {t('fleetPartner')}
                </button>
              </>
            )}
            {isLoggedIn && (
              <button
                onClick={() => {
                  navigateToDashboard();
                  setIsMenuOpen(false);
                }}
                className="bg-white text-black px-4 py-2 rounded-md font-bold hover:bg-gray-200 transition text-sm leading-none"
              >
                {t('dashboard')}
              </button>
            )}
          </nav>

          {/* Right Side Controls */}
          <div className="flex items-center gap-3 h-full">
            <LanguageSelector />

            {/* Hamburger Menu Button (Always Available) */}
            <button 
              onClick={toggleMenu} 
              className="text-white hover:text-gray-300 focus:outline-none p-2.5 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-md transition flex items-center justify-center cursor-pointer"
              aria-label="Toggle Navigation"
            >
              {isMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
            </button>

            {/* Dropdown Menu */}
            {isMenuOpen && (
              <div 
                ref={menuRef}
                className="absolute top-16 right-4 w-72 glass-menu rounded-md shadow-2xl border border-white/10 overflow-hidden py-3 text-white flex flex-col z-50 animate-in fade-in slide-in-from-top-4 duration-200"
              >
                <div className="px-4 py-2 border-b border-white/10 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#F9B101] opacity-80">Platform Navigation</span>
                </div>

                {isLoggedIn && (
                  <button
                    onClick={() => {
                      navigateToDashboard();
                      setIsMenuOpen(false);
                    }}
                    className="dropdown-item group text-[#F9B101]"
                  >
                    <FaThLarge className="item-icon group-hover:scale-110 transition-transform" />
                    <span>Go to Dashboard</span>
                  </button>
                )}

                <button
                  onClick={() => { navigate('/about'); setIsMenuOpen(false); }}
                  className="dropdown-item group"
                >
                  <FaInfoCircle className="item-icon group-hover:text-[#F9B101] transition-colors" />
                  <span>{t('aboutUs')}</span>
                </button>

                <button
                  onClick={() => { navigate('/help'); setIsMenuOpen(false); }}
                  className="dropdown-item group"
                >
                  <FaQuestionCircle className="item-icon group-hover:text-[#F9B101] transition-colors" />
                  <span>{t('help')}</span>
                </button>

                <button
                  onClick={() => { navigate('/contact'); setIsMenuOpen(false); }}
                  className="dropdown-item group"
                >
                  <FaEnvelope className="item-icon group-hover:text-[#F9B101] transition-colors" />
                  <span>{t('contactUs')}</span>
                </button>

                <div className="px-4 py-2 border-b border-white/10 my-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#F9B101] opacity-80">{t('servicesNav') || 'Services'}</span>
                </div>

                <button
                  onClick={() => { handleClientOnlyAction('/water-tanker'); setIsMenuOpen(false); }}
                  className="dropdown-item group"
                >
                  <span>{t('waterTankerServices')}</span>
                </button>

                <button
                  onClick={() => { handleClientOnlyAction('/general-heavy-trucks'); setIsMenuOpen(false); }}
                  className="dropdown-item group"
                >
                  <span>{t('bookTruck')}</span>
                </button>

                <button
                  onClick={() => { handleClientOnlyAction('/general-equipment-rentals'); setIsMenuOpen(false); }}
                  className="dropdown-item group"
                >
                  <span>{t('equipmentRentals') || 'Equipment Rentals'}</span>
                </button>

                <button
                  onClick={() => { handleClientOnlyAction('/general-construction-vehicles'); setIsMenuOpen(false); }}
                  className="dropdown-item group"
                >
                  <span>{t('construction')}</span>
                </button>

                <button
                  onClick={() => { window.open('https://karossstore.in', '_blank'); setIsMenuOpen(false); }}
                  className="dropdown-item group"
                >
                  <span>{t('gpsTrackers')}</span>
                </button>

                {isLoggedIn && (
                  <>
                    <div className="px-4 py-2 border-b border-white/10 my-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-[#F9B101] opacity-80">Account</span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="dropdown-item group text-red-500 hover:bg-red-500/10"
                    >
                      <FaSignOutAlt className="item-icon group-hover:translate-x-1 transition-transform" />
                      <span>{t('logout')}</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section (Full Screen Viewport min-h-screen) */}
      <section className="relative min-h-screen flex flex-col justify-center items-center pt-24 pb-16 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-50">
          <img 
            src="/bobcathero.png" 
            alt="KaroTrukk Hero Background" 
            className="w-full h-full object-cover object-center" 
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-center flex flex-col items-center w-full overflow-hidden">
          <h1 className="text-sm sm:text-3xl md:text-6xl lg:text-7xl font-black mb-2 sm:mb-4 tracking-tight leading-tight max-w-full px-2">
            {t('heroTitle1')} <br className="hidden sm:block" /> <span className="text-[#F9B101]">{t('heroTitle2')}</span>
          </h1>
          <p className="text-xs sm:text-lg md:text-2xl text-gray-300 max-w-2xl mx-auto mb-5 sm:mb-8 font-medium leading-relaxed px-3">
            {t('heroSub')}
          </p>



          <div className="flex flex-col sm:flex-row justify-center gap-3.5 w-full max-w-md sm:max-w-none">
            <button onClick={handleClientAction} className="w-full sm:w-auto bg-white text-black px-8 py-3.5 rounded-md text-base font-bold hover:bg-gray-100 transition shadow-sm">
              {t('bookVehicle')}
            </button>
            <button onClick={handleFleetPartnerAction} className="w-full sm:w-auto bg-[#F9B101] text-black px-8 py-3.5 rounded-md text-base font-bold hover:bg-[#e09f00] transition shadow-sm">
              {t('joinFleet')}
            </button>
          </div>
        </div>
      </section>

      {/* Feature Section: Bold Headline */}
      <section className="py-8 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div>
              <h2 className="text-2xl sm:text-4xl md:text-6xl font-extrabold text-black leading-tight mb-3 sm:mb-4">
                {t('whatWeDo') || 'What We Do'}
              </h2>
              <p className="text-sm sm:text-xl text-gray-600 mb-4 sm:mb-6 max-w-lg leading-relaxed">
                {t('whatWeDoDesc') || 'Cab-style hail booking for JCBs and equipment machineries in just a few steps. Fast, verified, and direct.'}
              </p>
            </div>
            <div className="bg-gray-100 rounded-lg p-4 sm:p-8 shadow-inner min-h-[240px] sm:min-h-[400px] flex items-center justify-center relative overflow-hidden">
              {/* Browser Window Placeholder */}
              <div className="w-full h-full bg-white rounded-md shadow-lg border border-gray-200 flex flex-col overflow-hidden">
                <div className="h-7 sm:h-8 bg-gray-50 border-b flex items-center px-3 sm:px-4 gap-1.5 sm:gap-2">
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400"></div>
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 bg-gray-50 overflow-hidden relative group min-h-[180px]">
                  <img
                    src={dashboardPreview}
                    alt="KaroTrukk Dashboard Preview"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                    width="1200"
                    height="800"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Rental Section */}
      <section id="equipment-rentals" className="equipment-section py-8 sm:py-12">
        <div className="equipment-header flex justify-between items-center px-4 sm:px-6">
          <h3 className="text-lg sm:text-2xl font-bold">{t('everydayJobsTitle') || 'Everyday Jobs We Help With'}</h3>
          <button 
            onClick={handleClientAction} 
            className="text-black hover:text-gray-700 font-bold text-xs sm:text-sm flex items-center gap-1 bg-gray-100 px-3 py-1.5 rounded-md border border-gray-300 transition active:scale-95 cursor-pointer shrink-0"
          >
            {t('moreArrow') || 'More →'}
          </button>
        </div>
        <div 
          ref={scrollRef} 
          onScroll={handleCardScroll}
          className="equipment-scroll-container px-4 sm:px-6"
        >
          {equipmentData.map((item) => (
            <div
              key={item.id}
              className="equipment-card"
              onClick={() => handleClientOnlyAction(`/equipment/${item.category}`)}
            >
              {item.image ? (
                <div className="equipment-image-container">
                  <img src={item.image} alt={item.title} className="equipment-image" loading="lazy" width="400" height="300" />
                </div>
              ) : (
                <div className="equipment-image-placeholder"></div>
              )}
              <div>
                <div className="equipment-category">{item.category}</div>
                <h4 className="equipment-title">{item.title}</h4>
              </div>
            </div>
          ))}

          {/* More Arrow Card at End of Scroll Container */}
          <div
            className="equipment-card flex flex-col items-center justify-center bg-[#F9B101]/10 border-2 border-dashed border-[#F9B101]/30 hover:border-[#F9B101] hover:bg-[#F9B101]/20 transition cursor-pointer text-center p-4 sm:p-6"
            onClick={handleClientAction}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-md bg-[#F9B101] text-black flex items-center justify-center text-lg sm:text-xl font-bold mb-2 sm:mb-3 shadow-sm">
              →
            </div>
            <span className="font-black text-gray-900 text-sm sm:text-base">{t('moreVehicles') || 'More Vehicles'}</span>
            <span className="text-[10px] sm:text-xs text-[#9B6E00] font-bold mt-0.5 sm:mt-1">{t('signInToViewAll') || 'Sign in to View All →'}</span>
          </div>
        </div>

        {/* Custom Touch & Hold Horizontal Scrollbar Below Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-3 sm:mt-4 flex flex-col items-center gap-1">
          <div 
            onClick={handleTouchBarClick}
            className="w-40 sm:w-64 h-2 sm:h-2.5 bg-gray-200 hover:bg-gray-300 rounded-sm overflow-hidden cursor-pointer relative shadow-inner touch-none transition-colors"
            title="Touch and drag to scroll cards"
          >
            <div 
              className="h-full bg-[#F9B101] rounded-sm transition-all duration-75 shadow-sm"
              style={{ width: '30%', transform: `translateX(${scrollProgress * 2.33}%)` }}
            />
          </div>
          <span className="text-[9px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
            {t('touchAndDragToScroll') || '← Touch & Drag to Scroll →'}
          </span>
        </div>
      </section>

      {/* Client vs Fleet Partner Info Section */}
      <section className="py-6 sm:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-5 sm:gap-6">
          <div className="bg-gray-50 p-5 sm:p-8 rounded-lg border border-gray-100">
            <h3 className="text-xl sm:text-3xl font-bold mb-2 sm:mb-3 text-black">{t('forCustomers') || 'For Customers'}</h3>
            <p className="text-gray-600 leading-relaxed text-sm sm:text-lg">
              {t('forCustomersDesc') || 'Book JCBs and equipment machineries on demand. Submit your request digitally and get connected in minutes.'}
            </p>
          </div>
          <div className="bg-gray-50 p-5 sm:p-8 rounded-lg border border-gray-100">
            <h3 className="text-xl sm:text-3xl font-bold mb-2 sm:mb-3 text-black">{t('forFleetPartners') || 'For Fleet Partners'}</h3>
            <p className="text-gray-600 leading-relaxed text-sm sm:text-lg">
              {t('forFleetPartnersDesc') || 'List your JCBs and equipment machineries. Receive direct booking requests and maximize daily utilization.'}
            </p>
          </div>
        </div>
      </section>

      {/* How KaroTrukk Works Section */}
      <section className="py-12 sm:py-16 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-left mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-black tracking-tight mb-2">
              {t('howWeConnect') || 'How We Connect'}
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 font-medium">
              {t('howWeConnectDesc') || 'JCB and equipment booking made simple in 4 steps.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { num: "01", title: t('step1Title') || "Choose Machinery", desc: t('step1Desc') || "Select the JCB or equipment machinery needed for your work." },
              { num: "02", title: t('step2Title') || "Request Booking", desc: t('step2Desc') || "Submit your requirement in a few steps." },
              { num: "03", title: t('step3Title') || "Get Connected", desc: t('step3Desc') || "Matched with verified available fleet partners." },
              { num: "04", title: t('step4Title') || "Get Work Moving", desc: t('step4Desc') || "Confirm your booking and start work on site." }
            ].map((step, idx) => (
              <div key={idx} className="bg-white p-6 rounded-lg border border-gray-200 shadow-xs flex flex-col justify-between text-left">
                <div>
                  <span className="text-2xl font-black text-[#9B6E00] block mb-3 font-mono">{step.num}</span>
                  <h4 className="text-base font-extrabold text-black mb-1.5">{step.title}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed font-medium">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Water Tanker Rental Service Section */}
      <section className="py-8 sm:py-14 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row items-center gap-6 sm:gap-8">
          <div className="lg:w-1/2 text-left w-full">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-black leading-tight mb-1 sm:mb-2">
              {t('waterTankersOnDemand') || 'Water Tankers On Demand'}
            </h2>
            <h3 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-600 mb-3 sm:mb-6">
              {t('waterSupplySub') || 'Home, Construction & Commercial Water Supply'}
            </h3>
            <p className="text-sm sm:text-xl text-gray-700 mb-4 sm:mb-6 leading-relaxed">
              {t('waterSupplyDesc') || 'Fast water tanker delivery for homes, swimming pools, borewell support, and construction sites with verified local fleet partners.'}
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4 mb-5 sm:mb-6 text-left">
              {[
                t('fastLocalDispatch') || "Fast Local Dispatch",
                t('verifiedTankerFleet') || "Verified Tanker Fleet",
                t('transparentPricing') || "Transparent Pricing",
                t('liveDriverTracking') || "Live Driver Tracking"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm sm:text-lg font-medium text-gray-800">
                  <span className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleClientOnlyAction('/water-tanker')}
              className="w-full sm:w-auto bg-blue-600 text-white px-8 sm:px-10 py-3 sm:py-3.5 rounded-md text-base sm:text-lg font-bold hover:bg-blue-700 transition shadow-sm text-center"
            >
              {t('orderTanker') || 'Order Tanker'}
            </button>
          </div>
          <div className="lg:w-1/2 relative flex justify-center w-full mt-4 sm:mt-0">
            <div className="relative z-10 w-full flex justify-center">
              <img
                src={waterTankerImg}
                alt="Water Tanker Truck"
                className="w-full max-w-xs sm:max-w-md h-auto object-contain"
                loading="lazy"
                width="800"
                height="600"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who Uses KaroTrukk Section */}
      <section className="py-10 sm:py-14 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-left mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-black tracking-tight mb-2">
              {t('whoUsesKaroTrukk') || 'Who Uses KaroTrukk?'}
            </h2>
            <p className="text-sm text-gray-600 font-medium">
              {t('whoUsesKaroTrukkDesc') || 'Serving diverse customer needs across residential, commercial, and site projects.'}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 text-left">
            {[
              { id: 'homeowners', label: t('homeowners') || "Homeowners" },
              { id: 'builders', label: t('builders') || "Builders" },
              { id: 'contractors', label: t('contractors') || "Contractors" },
              { id: 'factories', label: t('factories') || "Factories" },
              { id: 'warehouses', label: t('warehouses') || "Warehouses" },
              { id: 'shops', label: t('shops') || "Shops" },
              { id: 'eventOrganizers', label: t('eventOrganizers') || "Event Organizers" },
              { id: 'farmers', label: t('farmers') || "Farmers" }
            ].map((item, idx) => (
              <div key={idx} className="bg-gray-50 p-4 rounded-md border border-gray-200 flex items-center justify-center font-bold text-xs text-black text-center">
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Big Idea / Three Cards Section */}
      <section className="py-8 sm:py-12 relative overflow-hidden">
        {/* Background Image & Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/generalconstructionbg.webp"
            alt="KaroTrukk Construction Machinery Booking"
            className="w-full h-full object-cover object-center opacity-70"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-6 items-start relative z-10">
          {/* Left: Big Headline */}
          <div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight">
              {t('whyWeDoIt') || 'Why We Do It'}
            </h2>
            <p className="text-gray-300 text-sm sm:text-lg mt-3 leading-relaxed">
              {t('whyWeDoItDesc') || 'Construction and infrastructure projects depend on timely access to the right equipment. KaroTrukk was created to make machinery access simpler, transparent and digitally accessible across India.'}
            </p>
          </div>

          {/* Right: Cards */}
          <div className="space-y-3 sm:space-y-4 mt-2 sm:mt-0">
            {/* Card 1 */}
            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-lg p-4 sm:p-6 hover:bg-white/20 transition duration-300">
              <div>
                <h4 className="text-base sm:text-lg font-bold text-white mb-1">{t('ourMission') || 'Our Mission'}</h4>
                <p className="text-gray-300 leading-relaxed text-xs sm:text-sm">
                  {t('ourMissionDesc') || 'To simplify access to construction machinery and heavy equipment through an accessible, dependable booking experience.'}
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-lg p-4 sm:p-6 hover:bg-white/20 transition duration-300">
              <div>
                <h4 className="text-base sm:text-lg font-bold text-white mb-1">{t('ourVision') || 'Our Vision'}</h4>
                <p className="text-gray-300 leading-relaxed text-xs sm:text-sm">
                  {t('ourVisionDesc') || 'To build a trusted platform that makes construction machinery booking more convenient across India.'}
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-lg p-4 sm:p-6 hover:bg-white/20 transition duration-300">
              <div>
                <h4 className="text-base sm:text-lg font-bold text-white mb-1">{t('ourCommitment') || 'Our Commitment'}</h4>
                <p className="text-gray-300 leading-relaxed text-xs sm:text-sm">
                  {t('ourCommitmentDesc') || 'Built around convenience. Focused on reliable service, upfront clarity, and verified fleet partners.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are / Available Services Section */}
      <section className="py-8 sm:py-14 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row items-center gap-6 sm:gap-8">
          <div className="lg:w-1/2 text-left w-full">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-black leading-tight mb-2 sm:mb-4">
              {t('whoWeAre') || 'Who We Are'}
            </h2>
            <p className="text-sm sm:text-xl text-gray-700 mb-4 sm:mb-6 leading-relaxed">
              {t('whoWeAreSub') || "India's first cab-style hail booking platform for JCBs and equipment machineries."}
            </p>
            <ul className="space-y-2.5 sm:space-y-4 mb-5 sm:mb-6">
              <li className="flex items-center gap-2.5 sm:gap-3 text-sm sm:text-lg font-medium">
                <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#F9B101]/20 text-[#9B6E00] rounded-sm flex items-center justify-center text-xs sm:text-sm font-bold">✓</span>
                {t('jcbsEarthmoving') || 'JCBs & Earthmoving Equipment'}
              </li>
              <li className="flex items-center gap-2.5 sm:gap-3 text-sm sm:text-lg font-medium">
                <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#F9B101]/20 text-[#9B6E00] rounded-sm flex items-center justify-center text-xs sm:text-sm font-bold">✓</span>
                {t('equipmentMachineries') || 'Equipment Machineries'}
              </li>
              <li className="flex items-center gap-2.5 sm:gap-3 text-sm sm:text-lg font-medium">
                <span className="w-5 h-5 sm:w-6 sm:h-6 bg-[#F9B101]/20 text-[#9B6E00] rounded-sm flex items-center justify-center text-xs sm:text-sm font-bold">✓</span>
                {t('verifiedFleetPartners') || 'Verified Fleet Partners'}
              </li>
            </ul>
            <button
              onClick={handleClientAction}
              className="w-full sm:w-auto inline-block bg-black text-white px-8 sm:px-10 py-3 sm:py-3.5 rounded-md text-base sm:text-lg font-bold hover:bg-gray-800 transition shadow-sm text-center"
            >
              {t('bookVehicle') || 'Book Machinery'}
            </button>
          </div>
          <div className="lg:w-1/2 relative flex justify-center w-full mt-4 sm:mt-0">
            <div className="relative z-10 w-full max-w-xs sm:max-w-sm">
              <img
                src={gpsProduct}
                alt="KaroTrukk Construction Machinery"
                className="w-full h-auto rounded-lg shadow-md"
                loading="lazy"
                width="600"
                height="600"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Testimonials Section */}
      <section className="py-8 sm:py-14 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-6 sm:mb-8 text-left">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-black mb-2 sm:mb-4">{t('testimonials')}</h2>
          <p className="text-sm sm:text-xl text-gray-600">{t('testimonialsSub')}</p>
        </div>

        {/* Marquee/Slider */}
        <div className="testimonial-slider-container">
          <div className="testimonial-track">
            {[...testimonials, ...testimonials].map((item, index) => (
              <div key={index} className="testimonial-card">
                <p className="text-sm sm:text-lg font-medium text-gray-800 mb-4 sm:mb-6 leading-relaxed">
                  "{item.text}"
                </p>
                <div className="flex items-center gap-3 sm:gap-4 mt-auto">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-md bg-[#F9B101]/20 flex items-center justify-center text-xl sm:text-2xl shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-black text-xs sm:text-sm">{item.author}</h4>
                    <p className="text-[10px] sm:text-xs text-gray-500">{item.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Restriction Popup */}
      <RestrictionPopup 
        isOpen={showRestriction}
        onClose={() => setShowRestriction(false)}
        message={restrictionMessage}
      />
    </div>
  );
};

export default LandingPage;
